import React, { useState, useMemo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  MenuItem,
  Box,
  InputAdornment,
  Pagination,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ExpandMore, FilterList } from "@mui/icons-material";
import dayjs from "dayjs";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};

interface Booking {
  date: string;
  session: string;
  client: string;
  chips: number;
  duration?: string;
  mode?: "live" | "recorded";
  chipType?: "virtual" | "bonus";
}

interface Props {
  bookings: Booking[];
  chipsFromEarnings: number;
}

const BOOKINGS_PER_PAGE = 8;

export const TalentWalletBookingTableSection: React.FC<Props> = ({
  bookings,
  chipsFromEarnings,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sessionFilter, setSessionFilter] = useState("");
  const [sortByChips, setSortByChips] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const filteredBookings = useMemo(() => {
    return bookings
      .filter(
        (b) =>
          b.client.toLowerCase().includes(searchTerm.toLowerCase()) &&
          (sessionFilter
            ? b.session.toLowerCase().includes(sessionFilter.toLowerCase())
            : true)
      )
      .sort((a, b) =>
        sortByChips === "asc" ? a.chips - b.chips : b.chips - a.chips
      );
  }, [bookings, searchTerm, sessionFilter, sortByChips]);

  const paginatedBookings = useMemo(() => {
    const start = (page - 1) * BOOKINGS_PER_PAGE;
    return filteredBookings.slice(start, start + BOOKINGS_PER_PAGE);
  }, [filteredBookings, page]);

  const totalPages = Math.ceil(filteredBookings.length / BOOKINGS_PER_PAGE);

  const chipTypeSummary = useMemo(() => {
    return bookings.reduce(
      (acc, b) => {
        acc[b.chipType || "virtual"] += b.chips;
        return acc;
      },
      { virtual: 0, bonus: 0 }
    );
  }, [bookings]);

  const getRowStyle = (chipType?: string, mode?: string) => {
    if (chipType === "bonus")
      return { backgroundColor: theme.palette.warning.light + "22" };
    if (mode === "recorded")
      return { backgroundColor: theme.palette.info.light + "22" };
    return {};
  };

  return (
    <Accordion
      defaultExpanded
      sx={{
        borderRadius: 3,
        mb: 4,
        backgroundColor: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ color: COLORS.text }} />}
        sx={{ backgroundColor: "rgba(255,255,255,0.06)" }}
      >
        <Typography fontWeight={600} sx={{ color: COLORS.text }}>
          📋 Booking History
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: COLORS.panel }}>
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            backgroundColor: "rgba(255,255,255,0.06)",
            border: `1px solid ${COLORS.border}`,
          }}
        >
          {/* Summary */}
          <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
            <Chip
              label={`🎯 Virtual Chips: ${chipsFromEarnings}`}
              color="info"
              variant="outlined"
            />
            <Chip
              label={`🌟 Bonus Chips: ${chipTypeSummary.bonus}`}
              color="warning"
              variant="outlined"
            />
          </Box>

          {/* Filters */}
          <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
            <TextField
              size="small"
              label="Search by Client"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterList fontSize="small" sx={{ color: COLORS.muted }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                minWidth: 200,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255,255,255,0.06)",
                  color: COLORS.text,
                  "& fieldset": {
                    borderColor: COLORS.border,
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255,255,255,0.2)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: COLORS.red,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: COLORS.muted,
                },
                "& .MuiInputBase-input": {
                  color: COLORS.text,
                },
              }}
            />
            <TextField
              size="small"
              select
              label="Session Type"
              value={sessionFilter}
              onChange={(e) => {
                setSessionFilter(e.target.value);
                setPage(1);
              }}
              sx={{
                minWidth: 160,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255,255,255,0.06)",
                  color: COLORS.text,
                  "& fieldset": {
                    borderColor: COLORS.border,
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255,255,255,0.2)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: COLORS.red,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: COLORS.muted,
                },
                "& .MuiSvgIcon-root": {
                  color: COLORS.text,
                },
              }}
            >
              <MenuItem value="" sx={{ backgroundColor: COLORS.panel, color: COLORS.text }}>
                All
              </MenuItem>
              <MenuItem value="coffee" sx={{ backgroundColor: COLORS.panel, color: COLORS.text }}>
                Coffee
              </MenuItem>
              <MenuItem value="1:1" sx={{ backgroundColor: COLORS.panel, color: COLORS.text }}>
                1:1
              </MenuItem>
              <MenuItem value="panel" sx={{ backgroundColor: COLORS.panel, color: COLORS.text }}>
                Panel
              </MenuItem>
            </TextField>
            <TextField
              size="small"
              select
              label="Sort by Chips"
              value={sortByChips}
              onChange={(e) => {
                setSortByChips(e.target.value as "asc" | "desc");
                setPage(1);
              }}
              sx={{
                minWidth: 160,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255,255,255,0.06)",
                  color: COLORS.text,
                  "& fieldset": {
                    borderColor: COLORS.border,
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255,255,255,0.2)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: COLORS.red,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: COLORS.muted,
                },
                "& .MuiSvgIcon-root": {
                  color: COLORS.text,
                },
              }}
            >
              <MenuItem value="desc" sx={{ backgroundColor: COLORS.panel, color: COLORS.text }}>
                High to Low
              </MenuItem>
              <MenuItem value="asc" sx={{ backgroundColor: COLORS.panel, color: COLORS.text }}>
                Low to High
              </MenuItem>
            </TextField>
          </Box>

          {/* Table */}
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                <TableRow>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 600 }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 600 }}>
                    Session
                  </TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 600 }}>
                    Client
                  </TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 600 }}>
                    Duration
                  </TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 600 }}>
                    Mode
                  </TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 600 }}>
                    Chip Type
                  </TableCell>
                  <TableCell align="right" sx={{ color: COLORS.text, fontWeight: 600 }}>
                    Chips
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <Typography variant="body2" sx={{ color: COLORS.muted }}>
                        No matching bookings found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedBookings.map((b, idx) => (
                    <TableRow
                      key={idx}
                      hover
                      sx={{
                        transition: "background 0.2s ease",
                        backgroundColor:
                          idx % 2 === 0 ? COLORS.panel : "rgba(255,255,255,0.03)",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1)",
                        },
                        ...getRowStyle(b.chipType, b.mode),
                      }}
                    >
                      <TableCell sx={{ color: COLORS.text }}>
                        {dayjs(b.date).format("MMM DD")}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.text }}>{b.session || "—"}</TableCell>
                      <TableCell sx={{ color: COLORS.text }}>{b.client}</TableCell>
                      <TableCell sx={{ color: COLORS.text }}>{b.duration || "—"}</TableCell>
                      <TableCell sx={{ color: COLORS.text }}>{b.mode || "live"}</TableCell>
                      <TableCell sx={{ color: COLORS.text }}>
                        {b.chipType || "virtual"}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#10b981", fontWeight: 600 }}>
                        +{b.chips}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, val) => setPage(val)}
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: COLORS.text,
                    "&.Mui-selected": {
                      backgroundColor: COLORS.red,
                      color: COLORS.text,
                      "&:hover": {
                        backgroundColor: COLORS.red,
                      },
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  },
                }}
              />
            </Box>
          )}
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
};
